import { useState, useCallback, useEffect } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { X, FileText } from "lucide-react";
import { convertPdfsToJpgs, PdfToJpgCancelled, type PdfToJpgOptions, type PdfToJpgResult } from "./operations";
import { useRunToken } from "@/hooks/useRunToken";
import { formatFileSize } from "@/lib/formatFileSize";
import { dpiForScale, pagesLabel } from "./jpgNaming";
import { loadPdfDocument } from "./utils";
import { partitionReadable, unreadableText } from "./readablePdfs";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Converts each page of a PDF into a separate JPG or PNG image, useful for sharing a document as pictures, embedding a page in a presentation, or posting a page to social media where a PDF attachment isn't an option. Select several PDFs at once and each one is converted independently, keeping their pages organized separately rather than mixed together.`,
  how: `Upload one or more PDFs, choose a quality/resolution level and image format, and optionally a page range (like 1, 3-5 or 4-) when a single PDF is loaded. The dpi shown on each quality button is the resolution of the images: Standard is 108 dpi, High is 144 dpi, Maximum is 216 dpi and Print is 300 dpi, so pick Print when the images are going to paper. When the result is exactly one image (a one-page PDF, or a single page chosen from a longer one) it downloads as that image, named with its page number, e.g. report-page-03.jpg. Anything more downloads as one ZIP; with several PDFs each gets its own folder, and PDFs that share a name are kept apart as "name" and "name (2)". A PDF that can't be read is skipped and listed, so the rest still convert.`,
  tip: `For very text-heavy pages meant to stay readable at small sizes, a higher resolution and PNG (lossless) tend to hold up better than a lower-resolution JPEG once shared or resized elsewhere — at the cost of a larger file. If you only need one specific page as a real PDF page rather than an image, Extract Pages & Text is the more direct tool for that.`,
};

const QUALITY_PRESETS = [
  { label: "Standard", scale: 1.5, quality: 0.85 },
  { label: "High", scale: 2, quality: 0.92 },
  { label: "Maximum", scale: 3, quality: 0.98 },
  { label: "Print", scale: 300 / 72, quality: 0.98 },
];

function Previews({ result }: { result: PdfToJpgResult }) {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const made = result.previews.map((p) => URL.createObjectURL(p.blob));
    setUrls(made);
    return () => made.forEach((u) => URL.revokeObjectURL(u));
  }, [result]);
  const hidden = result.imageCount - result.previews.length;
  return (
    <div aria-label="Converted pages">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {result.previews.map((p, i) => (
          <figure key={`${i}-${p.sourceName}-${p.pageNum}`} className="rounded-lg border bg-card p-2">
            {urls[i] && <img src={urls[i]} alt={`${p.sourceName}, page ${p.pageNum}`} className="w-full h-40 object-contain bg-white rounded" />}
            <figcaption className="mt-1.5 text-xs text-muted-foreground">
              <span className="block truncate text-foreground" title={p.name}>{p.name}</span>
              {p.width} × {p.height} px · {formatFileSize(p.blob.size)}
            </figcaption>
          </figure>
        ))}
      </div>
      {hidden > 0 && <p className="mt-2 text-xs text-muted-foreground">…and {hidden} more {hidden === 1 ? "image" : "images"} in the download.</p>}
    </div>
  );
}

export default function PdfToJpg() {
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<PdfToJpgResult | null>(null);
  const [qualityIdx, setQualityIdx] = useState(1); // "High" by default
  const [format, setFormat] = useState<"image/jpeg" | "image/png">("image/jpeg");
  const [pageRange, setPageRange] = useState("");
  const runs = useRunToken();
  const formatName = format === "image/png" ? "PNG" : "JPG";

  const [unreadable, setUnreadable] = useState("");

  // A PDF that can't be opened is reported here, inline, and never added: nothing is converted or charged for it.
  const handleFiles = useCallback(async (newFiles: File[]) => {
    const { ok, bad } = await partitionReadable(newFiles, loadPdfDocument);
    setUnreadable(bad.length > 0 ? unreadableText(bad) : "");
    if (ok.length === 0) return;
    runs.cancel();
    setFiles((prev) => [...prev, ...ok]);
    setResult(null);
  }, [runs]);

  const removeFile = (index: number) => {
    runs.cancel();
    setProcessing(false);
    setProgress(null);
    setResult(null); // the old result still contained the removed file's pages
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) setPageRange("");
      return next;
    });
  };

  const changeOption = (apply: () => void) => {
    apply();
    setResult(null);
  };

  const convert = async () => {
    if (files.length === 0) return;
    const run = runs.start();
    setProcessing(true);
    setResult(null);
    setProgress(null);
    try {
      const preset = QUALITY_PRESETS[qualityIdx];
      const options: PdfToJpgOptions = {
        scale: preset.scale,
        quality: preset.quality,
        format,
        pageRange: files.length === 1 && pageRange.trim() ? pageRange : undefined,
        isCancelled: () => !runs.isCurrent(run),
      };
      const out = await convertPdfsToJpgs(files, (done, total) => { if (runs.isCurrent(run)) setProgress({ done, total }); }, options);
      if (!runs.isCurrent(run)) return;
      setResult(out);
      const what = `${pagesLabel(out.imageCount)} to ${formatName}`;
      if (out.skipped.length > 0) toast.warning(`Converted ${out.imageCount === 1 ? "1 page" : `${out.imageCount} pages`} from ${out.fileCount} of ${files.length} PDFs. Could not read: ${out.skipped.map((s) => s.name).join(", ")}`);
      else toast.success(`Converted ${what}`);
    } catch (error) {
      if (error instanceof PdfToJpgCancelled || !runs.isCurrent(run)) return;
      console.error(error);
      toast.error(error instanceof Error && error.message ? error.message : "Conversion failed. One of the PDFs may be corrupted or password-protected.");
    } finally {
      if (runs.isCurrent(run)) {
        setProcessing(false);
        setProgress(null);
      }
    }
  };

  const stop = () => {
    runs.cancel();
    setProcessing(false);
    setProgress(null);
    toast.info("Stopped");
  };

  const reset = () => {
    runs.cancel();
    setFiles([]);
    setUnreadable("");
    setResult(null);
    setPageRange("");
    setProcessing(false);
    setProgress(null);
  };

  return (
    <ToolContainer>
      {files.length === 0 ? (
        <>
        <FileDropZone gateAtDownload acceptDamagedPdf
          accept=".pdf"
          multiple
          onFiles={handleFiles}
          label="Drop one or more PDFs to convert to images"
          description="Every page becomes a high-quality image — processed entirely in your browser"
        />
        {unreadable && <p role="alert" className="mt-2 text-sm text-destructive">{unreadable}</p>}
        </>
      ) : (
        <div className="space-y-4">
          {unreadable && <p role="alert" className="text-sm text-destructive">{unreadable}</p>}
          {/* Above-fold trust strip. */}
          <div className="p-3 rounded-lg border bg-muted/50 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span>🔒 Runs entirely in your browser — never uploaded</span>
          </div>

          <div className="space-y-2">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
                <FileText className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm flex-1 truncate">{f.name}</span>
                <span className="text-xs text-muted-foreground">{formatFileSize(f.size)}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Remove ${f.name}`} onClick={() => removeFile(i)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>

          <fieldset disabled={processing} className="space-y-4 min-w-0 border-0 p-0 m-0">
            <div className="flex flex-wrap gap-4">
              <div>
                <Label className="mb-1 block text-sm">Quality (resolution)</Label>
                <div className="flex flex-wrap gap-2">
                  {QUALITY_PRESETS.map((p, i) => (
                    <Button key={p.label} type="button" size="sm" variant={qualityIdx === i ? "default" : "outline"} aria-pressed={qualityIdx === i} onClick={() => changeOption(() => setQualityIdx(i))}>
                      {p.label} · {dpiForScale(p.scale)} dpi
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Format</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant={format === "image/jpeg" ? "default" : "outline"} aria-pressed={format === "image/jpeg"} onClick={() => changeOption(() => setFormat("image/jpeg"))}>JPG</Button>
                  <Button type="button" size="sm" variant={format === "image/png" ? "default" : "outline"} aria-pressed={format === "image/png"} onClick={() => changeOption(() => setFormat("image/png"))}>PNG</Button>
                </div>
              </div>
            </div>

            <div className="max-w-xs">
              <Label htmlFor="pdf-jpg-range" className="mb-1 block text-sm">Page range (optional)</Label>
              <Input
                id="pdf-jpg-range"
                value={files.length === 1 ? pageRange : ""}
                disabled={files.length > 1}
                onChange={(e) => changeOption(() => setPageRange(e.target.value))}
                placeholder="Leave blank for every page (e.g. 1, 3-5, 8-)"
              />
              {files.length > 1 && (
                <p className="mt-1 text-xs text-muted-foreground">Page ranges work with one PDF at a time. With several PDFs, every page of each is converted — remove the others to pick pages.</p>
              )}
            </div>
          </fieldset>

          {processing ? (
            <ProcessingState
              message={progress && progress.total > 0 ? `Rendering page ${Math.min(progress.done + 1, progress.total)} of ${progress.total}...` : "Reading PDFs..."}
              progress={progress && progress.total > 0 ? (progress.done / progress.total) * 100 : undefined}
              onStop={stop}
            />
          ) : result ? (
            <>
              {result.skipped.length > 0 && (
                <NoticeBox role="alert" className="text-sm">
                  Converted {result.fileCount} of {files.length} PDFs. Could not read: {result.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}.
                </NoticeBox>
              )}
              {result.reducedPages > 0 && (
                <p className="text-xs text-muted-foreground">{pagesLabel(result.reducedPages)} {result.reducedPages === 1 ? "was" : "were"} very large, so the resolution was lowered to fit what the browser can draw.</p>
              )}
              <p className="text-sm text-muted-foreground">{pagesLabel(result.imageCount)} converted to {formatName}.</p>
              <div className="space-y-2 min-w-0">
                <DownloadButton
                  stacked
                  blob={result.blob}
                  fileName={result.filename}
                  label={result.imageCount === 1 ? `Download page as ${formatName}` : `Download all ${result.imageCount} pages (ZIP)`}
                  subtitle={[result.filename, pagesLabel(result.imageCount), result.imageCount === 1 ? formatName : `${formatName} images in a ZIP`, formatFileSize(result.blob.size)].join(" · ")}
                />
                <Button variant="link" className="h-11 min-h-11 px-0" onClick={reset}>Convert another PDF</Button>
              </div>
              <Previews result={result} />
            </>
          ) : (
            <div className="flex gap-3">
              <Button onClick={convert}>{files.length > 1 ? `Convert ${files.length} PDFs to ${formatName}` : `Convert to ${formatName}`}</Button>
              <Button variant="outline" onClick={reset}>Reset</Button>
            </div>
          )}
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
